import * as z from "zod";
import { v } from "~/domain/versioning";
import { clamp } from "~/lib/std";
import { SplitOrientation, VisualizationType } from "../constants";
import * as constraints from "../constraints";
import { imageFlipDirectionSchema, imageRotationSchema } from "./common";
import { layoutProfileDescriptorSchema_0_2_0 } from "./models.0-2-0";
import type * as sdkTypes from "./types";
import { createMigratableSchema, versionSchema } from "./utils";

export const V_0_3_0 = v(0, 3, 0);

const panelNodeDescriptorSchema: z.ZodType<sdkTypes.PanelNodeDescriptor> =
  z.object({
    id: z.number(),
    size: z.number(),
    panelId: z.number(),
  });

const containerNodeDescriptorSchema: z.ZodType<sdkTypes.ContainerNodeDescriptor> =
  z.object({
    id: z.number(),
    size: z.number(),
    orientation: z.nativeEnum(SplitOrientation),
    children: z.tuple([
      z.lazy(() => layoutNodeDescriptorSchema),
      z.lazy(() => layoutNodeDescriptorSchema),
    ]),
  });

const layoutNodeDescriptorSchema: z.ZodType<sdkTypes.LayoutNodeDescriptor> =
  z.union([panelNodeDescriptorSchema, containerNodeDescriptorSchema]);

const baseTopicDescriptorSchema = z.object({
  name: z.string(),
});

const basePanelDescriptorSchema = z.object({
  id: z.number(),
  title: z.string().nullable(),
});

const uninitializedPanelDescriptorSchema = basePanelDescriptorSchema.extend({
  visualization: z.literal(null),
  topics: z.tuple([]),
});

const timelineTopicDescriptorSchema = baseTopicDescriptorSchema;

const timelinePanelDescriptorSchema = basePanelDescriptorSchema.extend({
  visualization: z.literal(VisualizationType.Timeline),
  topics: z.tuple([timelineTopicDescriptorSchema]),
});

const chartTopicDescriptorSchema = baseTopicDescriptorSchema.extend({
  fields: z.array(z.string()).max(constraints.chartFields.max),
});

const chartPanelDescriptorSchema = basePanelDescriptorSchema.extend({
  visualization: z.literal(VisualizationType.Chart),
  topics: z.tuple([chartTopicDescriptorSchema]),
});

const imageTopicDescriptorSchema = baseTopicDescriptorSchema.extend({
  colorize: z.boolean(),
  brightnessPct: z
    .number()
    .min(constraints.imageBrightnessPct.min)
    .max(constraints.imageBrightnessPct.max),
  contrastPct: z
    .number()
    .min(constraints.imageContrastPct.min)
    .max(constraints.imageContrastPct.max),
  rotationDeg: imageRotationSchema,
  flip: imageFlipDirectionSchema.nullable(),
});

const imagePanelDescriptorSchema = basePanelDescriptorSchema.extend({
  visualization: z.literal(VisualizationType.Image),
  topics: z.tuple([imageTopicDescriptorSchema]),
});

const mapTopicDescriptorSchema = baseTopicDescriptorSchema;

const mapPanelDescriptorSchema = basePanelDescriptorSchema.extend({
  visualization: z.literal(VisualizationType.Map),
  topics: z.union([
    z.tuple([mapTopicDescriptorSchema]),
    z.tuple([mapTopicDescriptorSchema, mapTopicDescriptorSchema]),
  ]),
});

const pointCloudTopicDescriptorSchema = baseTopicDescriptorSchema.extend({
  size: z
    .number()
    .min(constraints.pointSize.min)
    .max(constraints.pointSize.max),
});

const threeDPanelDescriptorSchema = basePanelDescriptorSchema.extend({
  visualization: z.literal(VisualizationType.ThreeD),
  topics: z
    .array(pointCloudTopicDescriptorSchema)
    .min(constraints.threeDTopics.min)
    .max(constraints.threeDTopics.max),
});

const panelDescriptorSchema = z.union([
  uninitializedPanelDescriptorSchema,
  timelinePanelDescriptorSchema,
  chartPanelDescriptorSchema,
  imagePanelDescriptorSchema,
  mapPanelDescriptorSchema,
  threeDPanelDescriptorSchema,
]);

const baseLayoutProfileDescriptorSchema = z.object({
  name: z.string(),
  version: versionSchema(V_0_3_0),
  layout: layoutNodeDescriptorSchema,
  panels: z.array(panelDescriptorSchema),
}) satisfies z.ZodType<sdkTypes.LayoutProfileDescriptor, z.ZodTypeDef, unknown>;

export type LayoutProfileDescriptor_0_3_0 = z.infer<
  typeof baseLayoutProfileDescriptorSchema
>;

export const layoutProfileDescriptorSchema_0_3_0 = createMigratableSchema({
  next: baseLayoutProfileDescriptorSchema,
  previous: layoutProfileDescriptorSchema_0_2_0,
  migrate(value) {
    return {
      ...value,
      version: V_0_3_0.toString(),
      panels: value.panels.map((panel) => {
        if (panel.visualization !== VisualizationType.ThreeD) {
          return panel;
        } else {
          return {
            ...panel,
            topics: panel.topics.map((descriptor) => ({
              ...descriptor,
              size: convertPointSize(descriptor.size),
            })),
          };
        }
      }),
    };
  },
});

const PREVIOUS_MIN_POINT_SIZE = 0;
const PREVIOUS_MAX_POINT_SIZE = 0.75;

function convertPointSize(size: number): number {
  const normalizedSize =
    (size - PREVIOUS_MIN_POINT_SIZE) /
    (PREVIOUS_MAX_POINT_SIZE - PREVIOUS_MIN_POINT_SIZE);

  const newSize =
    normalizedSize * (constraints.pointSize.max - constraints.pointSize.min) +
    constraints.pointSize.min;

  return clamp(newSize, constraints.pointSize.min, constraints.pointSize.max);
}
